import { EditIcon, LinkIcon, TrashIcon } from "@xola/icons";
import { flash, Key } from "@xola/ui-kit";
import { produce } from "immer";
import { round } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Attachment, ProductObject } from "types";
import { AttachmentInstructionModal } from "./DescriptiveContent/AttachmentInstructionModal";
import { AttachmentModifyModal } from "./DescriptiveContent/AttachmentModifyModal";
import { getCredentialHeaders } from "@/api/credentialHeaders";
import { withUploadFileName } from "@/api/mediaApi";
import { xolaUri } from "@/api/xolaUri";
import { FileUploadInput } from "@/components/Common/FileUploadInput";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { PromptModal } from "@/components/Common/PromptModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { MERCHANDISE_API_PATH } from "@/models/Merchandise";

interface ProductAttachmentsUploadProps {
    attachments: Attachment[] | undefined;
    handleAttachmentChange: (attachments: Attachment[] | undefined) => any;
    productId: string;
    productType: ProductObject.EXPERIENCE | ProductObject.WARE;
}

export const ProductAttachmentsUpload = ({
    attachments = [],
    productId,
    productType,
    handleAttachmentChange,
}: ProductAttachmentsUploadProps) => {
    const invalidate = useInvalidate();

    const endpoint = productType === ProductObject.EXPERIENCE ? "experiences" : MERCHANDISE_API_PATH;
    const attachmentUrl = `${xolaUri}/api/${endpoint}/${productId}/attachments`;

    const [files, setFiles] = useState<Attachment[]>((attachments ?? []).filter((a) => a?.id));

    useEffect(() => {
        setFiles((attachments ?? []).filter((a) => a?.id));
    }, [attachments]);
    const instructionModal = useModal();
    const [currentAttachment, setCurrentAttachment] = useState<Attachment | null>(null);

    const { mutateAsync: uploadFile, isLoading } = useMutation(async (file: any) => {
        return fetch(withUploadFileName(attachmentUrl, file.name), {
            method: "POST",
            headers: getCredentialHeaders(),
            body: file,
        });
    });

    const { mutateAsync: updateFile, isLoading: isUpdateFileCallLoading } = useMutation(async (payload: any) => {
        return fetch(withUploadFileName(`${attachmentUrl}/${payload.id}`, payload.file.name), {
            method: "PUT",
            headers: getCredentialHeaders(),
            body: payload.file,
        });
    });

    const { mutateAsync: updateAttachment } = useMutation(async (attachment: Attachment) => {
        return fetch(`${attachmentUrl}/${attachment.id}`, {
            method: "PUT",
            headers: getCredentialHeaders(),
            body: JSON.stringify(attachment),
        });
    });

    const { mutateAsync: deleteAttachment } = useMutation(async (attachmentId: string) => {
        return fetch(`${attachmentUrl}/${attachmentId}`, {
            method: "DELETE",
            headers: getCredentialHeaders(),
        });
    });

    const handleFileUpload = async (file: Attachment) => {
        try {
            const response = await uploadFile(file);
            const responseJson = await response.json();
            if (![200, 201].includes(response.status)) {
                const errorMessage = responseJson?.field?.message || "Error in uploading attachment.";
                flash.danger(errorMessage);
                return;
            }

            // Merchandise API returns attachment directly, while Experience API wraps it in { attachment }
            const attachment = productType === ProductObject.WARE ? responseJson : responseJson.attachment;
            setCurrentAttachment(attachment);
            instructionModal.show();
            updateFiles([...files, attachment]);
        } catch {
            flash.danger("The file is not uploaded.");
        }
    };

    const handleFileModify = async (attachment: Attachment, modifiedFile: Attachment) => {
        try {
            const payload = {
                id: attachment.id,
                file: modifiedFile,
            };
            const response = await updateFile(payload);
            const responseJson = await response.json();
            if (response.status !== 200) {
                const errorMessage = responseJson?.field?.message || "Error in updating attachment.";
                flash.danger(errorMessage);
                return;
            }

            const updatedFiles = files.map((file) => {
                return file.id === attachment.id ? attachment : file;
            });
            updateFiles(updatedFiles);
        } catch {
            flash.danger("The file is not uploaded.");
        }
    };

    const handleInstructionChange = (instruction: string) => {
        dispatchUpdateInstructionCall(instruction);
        instructionModal.hide();
        invalidate(endpoint, productId);
    };

    const dispatchUpdateInstructionCall = async (instruction: string | null = null) => {
        const payload = currentAttachment;
        if (payload && instruction) {
            payload.caption = instruction;
            setCurrentAttachment(null);
            await updateAttachment(payload);
            const updatedFiles = files.map((file) => {
                return file.id === payload.id ? payload : file;
            });
            updateFiles(updatedFiles);
        }
    };

    const handleAttachmentDelete = async (attachmentId: string) => {
        await deleteAttachment(attachmentId);
        const updatedFiles = files.filter((file) => file.id !== attachmentId);
        updateFiles(updatedFiles);
    };

    const handleInstructionModify = async (payload: Attachment) => {
        await updateAttachment(payload);
        const updatedFiles = files.map((file) => {
            return file.id === payload.id ? payload : file;
        });
        updateFiles(updatedFiles);
    };

    const updateFiles = (updatedFiles: Attachment[]) => {
        setFiles(updatedFiles);
        handleAttachmentChange(updatedFiles);
        invalidate(endpoint, productId);
    };

    return (
        <>
            {files
                .filter((file) => file?.id)
                .map((file: Attachment) => (
                    <AttachmentItem
                        key={file.id}
                        file={file}
                        isLoading={isUpdateFileCallLoading}
                        handleAttachmentDelete={handleAttachmentDelete}
                        handleFileModify={handleFileModify}
                        handleInstructionModify={handleInstructionModify}
                    />
                ))}

            <FileUploadInput
                buttonLabel="Attach PDF"
                isLoading={isLoading}
                onChange={handleFileUpload}
                onError={flash.danger}
            />

            <AttachmentInstructionModal
                isOpen={instructionModal.isVisible}
                onClose={instructionModal.hide}
                onConfirm={(instruction) => handleInstructionChange(instruction)}
            />
        </>
    );
};

interface AttachmentItemProps {
    file: Attachment;
    isLoading: boolean;
    handleAttachmentDelete: (id: string) => void;
    handleFileModify: (attachment: Attachment, file: Attachment) => void;
    handleInstructionModify: (attachment: Attachment) => void;
}

const AttachmentItem = ({
    file,
    handleAttachmentDelete,
    isLoading,
    handleFileModify,
    handleInstructionModify,
}: AttachmentItemProps) => {
    const deleteModal = useModal();
    const editModal = useModal();

    const handleClose = () => {
        editModal.hide();
    };

    const handleDelete = () => {
        handleAttachmentDelete(file.id);
        deleteModal.hide();
    };

    const handleInstructionChange = (attachment: Attachment, instruction: string) => {
        const modifiedAttachment = produce(attachment, (attachment) => {
            attachment.caption = instruction;
        });
        handleInstructionModify(modifiedAttachment);
        editModal.hide();
    };

    return (
        <div
            key={file.id}
            className="mb-2 flex w-full justify-between rounded border border-gray-light p-4"
            data-testid="experience-attachments-item"
        >
            <div>
                <div className="mb-2 flex items-center space-x-2">
                    <LinkIcon />
                    <NoTranslate className="font-bold">{file.title}</NoTranslate>
                    <Key char={`${round(file.size / 1000, 1)}kB`} />
                </div>
                <NoTranslate className="text-gray-darker" data-testid="experience-attachments-item-instructions">
                    {file.caption}
                </NoTranslate>
            </div>
            <div className="space-x-2">
                <SecondaryButton size="small" data-testid="experience-attachments-edit-button" onClick={editModal.show}>
                    <EditIcon />
                </SecondaryButton>
                <SecondaryButton
                    size="small"
                    data-testid="experience-attachments-delete-button"
                    onClick={deleteModal.show}
                >
                    <TrashIcon />
                </SecondaryButton>

                <AttachmentModifyModal
                    file={file}
                    isOpen={editModal.isVisible}
                    isLoading={isLoading}
                    onFileModify={handleFileModify}
                    onInstructionChange={handleInstructionChange}
                    onClose={handleClose}
                />

                <PromptModal
                    title="WARNING"
                    isOpen={deleteModal.isVisible}
                    onConfirm={handleDelete}
                    onClose={deleteModal.hide}
                >
                    <div className="flex flex-col space-y-2">
                        <span>
                            You are deleting an attachment that could have already been sent to customers with future
                            bookings for this product as part of their confirmation email.
                        </span>
                        <span>They will no longer be able to view this attachment.</span>
                        <span>Are you sure you want to continue?</span>
                    </div>
                </PromptModal>
            </div>
        </div>
    );
};
